import React, { useState } from "react";
import {
    FormGroup,
    Label,
    Input,
    InputGroup,
    InputGroupAddon,
    InputGroupText,
    Button,
    Row,
    Col
} from "reactstrap";
import { changeCustomerPassword } from "actions/customer";
import { useEffect } from "react";

const changePasswordFields = {
    password: {
        value: "",
        error: "",
    },
    confirmPassword: {
        value: "",
        error: "",
    },
};

function ChangePassword({
    customerId,
    notify,
    hasAnyError,
    activeTab
}) {
    const [userPasswordData, setPasswordData] = useState({
        password: { value: "", error: "" },
        confirmPassword: { value: "", error: "" }
    });

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [showLoader, setShowLoader] = useState(false);

    useEffect(() => {
        if (activeTab !== "password") {
            setPasswordData(changePasswordFields);
            setShowPassword(false);
            setShowConfirmPassword(false);
        }
    }, [activeTab]);

    const handlePasswordInputChange = (fieldName, newValue) => {
        setPasswordData((prev) => ({
            ...prev,
            [fieldName]: {
                value: newValue,
                error: "",
            },
        }));
    };

    const getPasswordValidationError = (name, value) => {
        switch (name) {
            case "password":
                return !value
                    ? "Please enter a password"
                    : value.length < 8
                        ? "The password must contain 8 characters"
                        : userPasswordData.confirmPassword.value &&
                            value !== userPasswordData.confirmPassword.value
                            ? "The new password and confirm password must match"
                            : "";

            case "confirmPassword":
                return !value
                    ? "Please enter a confirm password"
                    : userPasswordData.password.value &&
                        value !== userPasswordData.password.value
                        ? "The new password and confirm password must match"
                        : "";

            default:
                return "";
        }
    };

    const handleOnBlurPassForm = (name, value) => {
        const err = getPasswordValidationError(name, value);
        setPasswordData((prev) => ({
            ...prev,
            [name]: { ...prev[name], error: err }
        }));
    };

    const handlePasswordSave = async () => {
        setShowLoader(true);

        const passErr = getPasswordValidationError(
            "password",
            userPasswordData.password.value
        );
        const confirmErr = getPasswordValidationError(
            "confirmPassword",
            userPasswordData.confirmPassword.value
        );

        if (passErr || confirmErr) {
            setPasswordData((prev) => ({
                password: { ...prev.password, error: passErr },
                confirmPassword: { ...prev.confirmPassword, error: confirmErr },
            }));
            return;
        }

        const payload = {
            newPassword: userPasswordData.password.value,
            confirmNewPassword: userPasswordData.confirmPassword.value,
        };

        const res = await changeCustomerPassword(customerId, payload);

        setShowLoader(false);

        if (res.code === 200) {
            notify("success", res?.message || "Password updated successfully.");
            setPasswordData(changePasswordFields);
        } else {
            notify("danger", res.message || "Failed to update password.");
        }
    };

    return (
        <>
            <Row className="justify-content-start my-3 px-3">
                <Col md="4">
                    <FormGroup
                        className={`has-label ${userPasswordData.password.error
                            ? "has-danger"
                            : ""
                            } d-flex flex-column align-items-start mb-4`}
                    >
                        <Label>
                            New Password{" "}
                            <span className="validation-error">*</span>
                        </Label>

                        <InputGroup className="input-group">
                            <Input
                                name="password"
                                type={showPassword ? "text" : "password"}
                                autoComplete="off"
                                placeholder="Enter new password"
                                value={userPasswordData.password.value}
                                onChange={(e) =>
                                    handlePasswordInputChange(
                                        "password",
                                        e.target.value
                                    )
                                }
                                onBlur={(e) =>
                                    handleOnBlurPassForm(
                                        "password",
                                        e.target.value
                                    )
                                }
                                maxLength={16}
                                className={
                                    userPasswordData.password.error
                                        ? "is-invalid"
                                        : ""
                                }
                            />
                            <InputGroupAddon addonType="append">
                                <InputGroupText
                                    className="pointer"
                                    onClick={() =>
                                        setShowPassword((prev) => !prev)
                                    }
                                >
                                    {showPassword ? (
                                        <i className="fa fa-eye-slash" />
                                    ) : (
                                        <i className="fa fa-eye" />
                                    )}
                                </InputGroupText>
                            </InputGroupAddon>
                        </InputGroup>

                        {userPasswordData.password.error && (
                            <small className="validation-error">
                                {userPasswordData.password.error}
                            </small>
                        )}
                    </FormGroup>

                    <FormGroup
                        className={`has-label ${userPasswordData.confirmPassword.error
                            ? "has-danger"
                            : ""
                            } d-flex flex-column align-items-start mb-4`}
                    >
                        <Label>
                            Confirm New Password{" "}
                            <span className="validation-error">*</span>
                        </Label>

                        <InputGroup className="input-group">
                            <Input
                                name="confirmPassword"
                                type={showConfirmPassword ? "text" : "password"}
                                autoComplete="off"
                                placeholder="Confirm new password"
                                value={userPasswordData.confirmPassword.value}
                                onChange={(e) =>
                                    handlePasswordInputChange(
                                        "confirmPassword",
                                        e.target.value
                                    )
                                }
                                onBlur={(e) =>
                                    handleOnBlurPassForm(
                                        "confirmPassword",
                                        e.target.value
                                    )
                                }
                                maxLength={16}
                                className={
                                    userPasswordData.confirmPassword.error
                                        ? "is-invalid"
                                        : ""
                                }
                            />
                            <InputGroupAddon addonType="append">
                                <InputGroupText
                                    className="pointer"
                                    onClick={() =>
                                        setShowConfirmPassword((prev) => !prev)
                                    }
                                >
                                    {showConfirmPassword ? (
                                        <i className="fa fa-eye-slash" />
                                    ) : (
                                        <i className="fa fa-eye" />
                                    )}
                                </InputGroupText>
                            </InputGroupAddon>
                        </InputGroup>

                        {userPasswordData.confirmPassword.error && (
                            <small className="validation-error">
                                {userPasswordData.confirmPassword.error}
                            </small>
                        )}
                    </FormGroup>

                    <div className="d-flex justify-content-start">
                        <Button
                            color="default"
                            type="submit"
                            className="px-4 shadow-sm"
                            onClick={handlePasswordSave}
                            disabled={hasAnyError(userPasswordData)}
                        >
                            {showLoader ? (
                                <i className="fa fa-spinner fa-spin" />
                            ) : (
                                "Save"
                            )}
                        </Button>
                    </div>
                </Col>
            </Row>
        </>
    )
}

export default ChangePassword;